import styled from "styled-components";
import H1Text from "../components/common/ui-components/h1Text";
import TableComponent, {
  Column,
} from "../components/common/ui-components/tableComponent";
import { useEffect, useMemo, useState } from "react";
import { SearchBar } from "../components/common/ui-components/searchBar";
import CustomButton from "../components/common/customButton";
import ModalComponent from "../components/common/modalComponent";
import DateModalComponent from "../components/common/ui-components/dateModal";
import AddressModal from "../components/common/ui-components/addressModal";
import { useNavigate } from "react-router-dom";
import CreatePlumberModal from "../components/common/ui-components/createPlumberModal";
import { getMattressCareList } from "../API/AppRepository";
import CreateMattressModal from "../components/common/ui-components/createMattressModal";

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  /* 모바일*/
  @media (max-width: 767px) {
    padding: 10px 20px;
    gap: 10px;
  }

  /* 태블릿*/
  @media (min-width: 768px) and (max-width: 1199px) {
    padding: 20px 50px;
    gap: 16px;
  }

  /* PC */
  @media (min-width: 1200px) {
    padding: 50px 80px;
    gap: 20px;
  }
`;

const HeaderArea = styled.div`
  width: 100%;
  display: flex;
  justify-content: start;
  align-items: center;
  /* 모바일*/
  @media (max-width: 767px) {
    height: 30px;
    justify-content: space-between;
  }

  /* 태블릿*/
  @media (min-width: 768px) and (max-width: 1199px) {
    height: 50px;
  }

  /* PC */
  @media (min-width: 1200px) {
    height: 50px;
  }
`;

const MobileButtonBox = styled.div`
  /* 모바일*/
  @media (max-width: 767px) {
    display: flex;
    height: 100%;
  }

  /* 태블릿*/
  @media (min-width: 768px) and (max-width: 1199px) {
    display: none;
  }

  /* PC */
  @media (min-width: 1200px) {
    display: none;
  }
`;

const ButtonBox = styled.div`
  /* 모바일*/
  @media (max-width: 767px) {
    display: none;
  }

  /* 태블릿*/
  @media (min-width: 768px) and (max-width: 1199px) {
    display: flex;
  }

  /* PC */
  @media (min-width: 1200px) {
    display: flex;
  }
`;

const TableArea = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SearchBarArea = styled.div`
  width: 100%;
  height: 42px;
  display: flex;
  justify-content: space-between;
  gap: 10px;
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 10px 14px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 300;
  color: white;
  background-color: ${({ $status }) => {
    switch ($status) {
      case "접수완료":
        return "#0C8CE9";
      case "상담토스":
        return "#14AE5C";
      case "상담승인":
        return "#f87171";
      case "계약미승인":
        return "#ff943d";
      case "계약완료":
        return "#05ca3d";
      case "완료":
        return "#05ca3d";
      case "취소":
        return "#9ca3af";
      default:
        return "#9ca3af";
    }
  }};
`;

const columnsData = [
  { key: "receivedDate", header: "접수일" },
  { key: "name", header: "고객명" },
  { key: "contact", header: "연락처" },
  { key: "workDate", header: "작업일" },
  { key: "address", header: "작업주소", rowAlign: "left" },
  { key: "status", header: "상태", type: "badge" },
];

const columns: Column<Record<string, any>>[] = columnsData.map((col) => ({
  key: col.key,
  header: col.header,
  rowAlign: col.rowAlign as "left" | "center" | "right" | undefined,
  render:
    col.type === "badge"
      ? (value) => <StatusBadge $status={value}>{value}</StatusBadge>
      : undefined,
}));

const statusMap: Record<string, string> = {
  "11": "접수완료",
  "12": "상담토스",
  "13": "상담승인",
  "14": "상담완료",
  "19": "상담반려",
  "21": "계약미승인",
  "22": "계약완료",
  "41": "완료",
  CA: "취소",
};
export default function PlumberPage() {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState<{ rows: any[] }>({ rows: [] });
  const [search, setSearch] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);

  const [form, setForm] = useState({
    name: "",
    statusCode: "",
    contact: "",
    itemCode: "P01531",
    address: "",
    addressDetail: "",
    zipCode: "",
    workDate: "",
    totalPrice: 0,
    memo: "",
  });

  const fetchData = async () => {
    const response: any = await getMattressCareList();
    if (response?.data?.result?.mattressReceiptList) {
      const mapped = response.data.result.mattressReceiptList.map(
        (item: any) => ({
          id: item.recNum,
          name: item.name,
          contact: item.contact,
          workDate: item.workDate?.slice(0, 10),
          receivedDate: item.receivedDate?.slice(0, 10),
          address: item.address,
          status: statusMap[item.status] || "알수없음",
        })
      );
      setTableData({ rows: mapped });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);

  const handleFormChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const filteredRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    let filtered = [...tableData.rows];

    if (keyword) {
      filtered = filtered.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(keyword)
        )
      );
    }

    if (sortConfig) {
      filtered.sort((a, b) => {
        const aVal = a[sortConfig.key as keyof typeof a];
        const bVal = b[sortConfig.key as keyof typeof b];

        if (aVal === bVal) return 0;

        return sortConfig.direction === "asc"
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      });
    }

    return filtered;
  }, [search, sortConfig, tableData.rows]);

  const goDetailPage = (id: string, data: any) => {
    try {
      navigate(`/mattress/mattressDetail/${id}`, {
        state: data,
      });
    } catch (e) {
      console.error("navigate error", e);
    }
  };
  return (
    <>
      <Wrapper>
        <HeaderArea>
          <H1Text>접수목록</H1Text>
          <MobileButtonBox>
            <CustomButton
              text="접수하기"
              $bgColor="#242A30"
              height="100%"
              width="120px"
              fontWeight="400"
              onClick={() => setIsCreateModalOpen(true)}
            />
          </MobileButtonBox>
        </HeaderArea>
        <TableArea>
          <SearchBarArea>
            <SearchBar search={search} setSearch={setSearch} />
            <ButtonBox>
              <CustomButton
                text="접수하기"
                $bgColor="#242A30"
                height="100%"
                width="120px"
                fontWeight="400"
                size="16px"
                onClick={() => setIsCreateModalOpen(true)}
              />
            </ButtonBox>
          </SearchBarArea>
          <TableComponent
            columns={columns}
            data={filteredRows}
            goDetailPage={goDetailPage}
          />
        </TableArea>
      </Wrapper>
      {isCreateModalOpen && (
        <ModalComponent
          title="간편 접수하기"
          onClose={() => setIsCreateModalOpen(false)}
          rightPanel={
            isAddressModalOpen ? (
              <AddressModal
                onSelect={({ address, zipCode }) => {
                  setForm((prev) => ({
                    ...prev,
                    address,
                    zipCode,
                  }));
                  setIsAddressModalOpen(false);
                }}
                onClose={() => setIsAddressModalOpen(false)}
              />
            ) : isDateModalOpen ? (
              <DateModalComponent
                initialDate={form.workDate}
                onSelect={(date) => {
                  setForm((prev) => ({ ...prev, workDate: date }));
                  setIsDateModalOpen(false);
                }}
                onClose={() => setIsDateModalOpen(false)}
              />
            ) : null
          }
        >
          <CreateMattressModal
            form={form}
            onChange={handleFormChange}
            onOpenAddressModal={() => setIsAddressModalOpen(true)}
            onOpenDateModal={() => setIsDateModalOpen(true)}
            setIsCreateModalOpen={setIsCreateModalOpen}
            onSuccess={fetchData}
          />
        </ModalComponent>
      )}
    </>
  );
}
